import { cx } from "@emotion/css";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import isMobile from "src/helpers/isMobile";
import CancelledIcon from "src/images/Icons/newIcons/exam/answers/cancelled.svg";
import CorrectIcon from "src/images/Icons/newIcons/exam/answers/correct.svg";
import WrongIconIcon from "src/images/Icons/newIcons/exam/answers/wrong.svg";
import SkipIcon from "src/images/Icons/newIcons/skip.svg";

interface StatProps {
	Icon: ComponentType<{ className?: string }>;
	title: string;
	score: number;
	scoreClassName: string;
	className: string;
	flexDirection: string;
}

const Stat = ({
	Icon,
	title,
	score,
	scoreClassName,
	className,
	flexDirection,
}: StatProps) => (
	<div className={className}>
		<div className={cx("flex items-center", flexDirection)}>
			<Icon
				className={cx("icon-black", isMobile() ? "icon-small" : "icon-normal")}
			/>
			<span className="size-16-light text-center">{title}</span>
		</div>
		<span className={cx("size-20-light", scoreClassName)}>{score}</span>
	</div>
);

interface ExamSimpleStats {
	correctAnswers: number;
	wrongAnswers: number;
	skippedQuestions: number;
	cancelledQuestions: number;
}

interface ExamSimpleStatisticsProps {
	stats: ExamSimpleStats;
	className?: string;
}

export const ExamSimpleStatistics = ({
	stats,
	className,
}: ExamSimpleStatisticsProps) => {
	const { t } = useTranslation();

	return (
		<div className={cx(className, "exam-simple-statistics")}>
			<Stat
				Icon={CorrectIcon}
				title={t("modals.statistics.correctAnswers")}
				score={stats.correctAnswers}
				scoreClassName="text-success"
				className="flex items-center justify-between"
				flexDirection="flex-row gap-8"
			/>
			<Stat
				Icon={WrongIconIcon}
				title={t("modals.statistics.wrongAnswers")}
				score={stats.wrongAnswers}
				scoreClassName="text-error"
				className="flex items-center justify-between"
				flexDirection="flex-row gap-8"
			/>
			{!!stats.skippedQuestions && (
				<Stat
					Icon={SkipIcon}
					title={t("modals.statistics.skippedAnswers")}
					score={stats.skippedQuestions}
					scoreClassName="text-primary"
					className="flex items-center justify-between"
					flexDirection="flex-row gap-8"
				/>
			)}
			<Stat
				Icon={CancelledIcon}
				title={t("modals.statistics.cancelledAnswers")}
				score={stats.cancelledQuestions}
				scoreClassName="text-dark-pink"
				className="flex items-center justify-between"
				flexDirection="flex-row gap-8"
			/>
		</div>
	);
};
